import { useEffect, useRef, useState } from "react";
import {
  BehaviorSubject,
  Subject,
  Observable,
  bufferWhen,
  concatMap,
  debounceTime,
  filter,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  takeUntil,
  tap,
  withLatestFrom,
  EMPTY,
} from "rxjs";
import { useAtomValue } from "jotai";
import { mainTipTapEditorAtom } from "../test-editor-gpt/tip-tap-editor/tipTapEditor";
import { useDeviceContext } from "@/routes/sessions/DeviceContext";
import { getHoveredElement } from "@/components/device-provider/utils";
import { sessionMutationAtom } from "@/utils/simulator";
import { appetizeSessionStore } from "@/atoms";
import { type DeviceContextState } from "@/routes/test-builder-page";
import { type DeviceScreen } from "@/routes/sessions/models";
import { useStore } from "@tanstack/react-store";

// ==================== Types ====================
interface TouchState {
  startTime: number;
  isTouching: boolean;
  isSwipe: boolean;
  startElementCoords: { x: number; y: number };
}

interface CursorPosition {
  x: number;
  y: number;
}

interface Trail {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

type EventSource = "user" | "external";

interface SimulatorEvent {
  source: EventSource;
  type: "text" | "space" | "delete" | "tap" | "swipe";
  data?: any;
  logData?: {
    command: string;
    screenshot?: string;
    editTexts?: any[];
  };
}

interface UseInteractionsParams {
  wrapperRef: React.RefObject<HTMLDivElement>;
  companyData: any;
  platform: string;
  deviceContextState?: DeviceContextState;
}

interface UseInteractionsReturn {
  // UI State
  isFocused: boolean;
  cursorPos: CursorPosition;
  trail: Trail | null;
  isTapping: boolean;
  hasStreamElement: boolean;
  // Methods
  selectRecordedContent: () => void;
}

// ==================== Constants ====================
const PRINTABLE_CHAR_REGEX = /^[\u0021-\u007E\u00A0-\u00FF]$/;
const SWIPE_THRESHOLD = 50;
const TAP_MOVEMENT_THRESHOLD = 10;
const TAP_TIMEOUT = 200;

// ==================== Main Hook ====================
export const useInteractions = ({
  wrapperRef,
  companyData,
  platform,
  deviceContextState,
}: UseInteractionsParams): UseInteractionsReturn => {
  // ========== React State ==========
  const [isFocused, setIsFocused] = useState(false);
  const [cursorPos, setCursorPos] = useState<CursorPosition>({ x: 0, y: 0 });
  const [trail, setTrail] = useState<Trail | null>(null);
  const [isTapping, setIsTapping] = useState(false);
  const [hasStreamElement, setHasStreamElement] = useState(false);
  const mainTipTapEditor = useAtomValue(mainTipTapEditorAtom);
  const { deviceScreen } = useDeviceContext();
  const deviceScreenRef = useRef<DeviceScreen | undefined>(undefined);

  const { data: gptDriverSimulatorData } = useAtomValue(sessionMutationAtom);
  const usingGptDriverSimulator = !!gptDriverSimulatorData;
  const appetizeSession = useStore(appetizeSessionStore, (state) => state);

  // Keep a ref to the current focus state for synchronous access
  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  useEffect(() => {
    deviceScreenRef.current = deviceScreen;
  }, [deviceScreen]);

  // ========== Recording State ==========
  const recordingStartPos = useRef<number | null>(null);
  const recordingEndPos = useRef<number | null>(null);
  const previousPromptGeneratorEnabled = useRef(
    deviceContextState?.recordInteractionsEnabled ?? false
  );

  // ========== RxJS Subjects ==========
  const destroy$ = useRef(new Subject<void>()).current;
  const touchState$ = useRef(
    new BehaviorSubject<TouchState>({
      startTime: 0,
      isTouching: false,
      isSwipe: false,
      startElementCoords: { x: 0, y: 0 },
    })
  ).current;

  // ========== Helper Functions ==========

  const promptGeneratorEnabled =
    deviceContextState?.recordInteractionsEnabled ?? false;

  /**
   * Adds content to Tiptap editor after the current cursor position
   * Automatically detects the current node type and maintains structure
   * Note: This works even when editor is not editable (during recording)
   */
  const addTipTapEditorContent = (content: string) => {
    if (!mainTipTapEditor) return;

    const { state, view } = mainTipTapEditor;
    const { $from } = state.selection;

    // Track if this is the first content during recording
    const isFirstRecordedContent =
      promptGeneratorEnabled && recordingStartPos.current === null;
    const isRecording = promptGeneratorEnabled;

    // Detect what type of node we're currently in
    let node;
    const parent = $from.parent;
    let insertionPos = null;
    let insertedNodeSize = 0;

    // Check if we're in a list by looking up the tree
    let listDepth = -1;
    let listItemDepth = -1;
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.type.name === "listItem") {
        listItemDepth = d;
      } else if (
        node.type.name === "bulletList" ||
        node.type.name === "orderedList"
      ) {
        listDepth = d;
        break;
      }
    }

    // If we found a list structure
    if (listDepth > -1 && listItemDepth > -1) {
      // First, check if the current list item is empty
      const currentListItem = $from.node(listItemDepth);
      const isCurrentListItemEmpty = currentListItem.textContent.trim() === "";

      if (isCurrentListItemEmpty) {
        // Current list item is empty, add content to it instead of creating a new one
        try {
          // Get the position inside the current list item's paragraph
          const paragraphPos = $from.pos;
          insertionPos = paragraphPos;

          // Insert text at current position
          const tr = state.tr.insertText(content, paragraphPos);
          insertedNodeSize = content.length;

          // Move cursor to end of inserted text
          const newPos = paragraphPos + content.length;
          if (newPos <= tr.doc.content.size) {
            tr.setSelection(
              state.selection.constructor.near(tr.doc.resolve(newPos))
            );
          }

          view.dispatch(tr);

          // Set recording start position if this is the first content
          if (isFirstRecordedContent) {
            recordingStartPos.current = insertionPos;
            console.log(
              "First recorded content starts at position:",
              recordingStartPos.current
            );
          }

          // Update recording end position if we're recording
          if (isRecording) {
            recordingEndPos.current = insertionPos + insertedNodeSize;
            console.log(
              "Updated recording end position to:",
              recordingEndPos.current
            );
          }
          return;
        } catch (error) {
          console.warn(
            "Failed to insert in current empty list item, creating new list item"
          );
          // Fall through to create new list item
        }
      }

      // Current list item is not empty, create a new list item
      node = state.schema.nodes.listItem.create(
        null,
        state.schema.nodes.paragraph.create(null, state.schema.text(content))
      );
      insertedNodeSize = node.nodeSize;

      // Get the position after the current list item - with bounds checking
      try {
        const afterListItem = $from.after(listItemDepth);
        insertionPos = afterListItem; // Track where we're inserting

        const tr = state.tr.insert(afterListItem, node);

        // Move cursor to the new list item
        const newPos = afterListItem + 2; // +2 to get inside the paragraph of the new list item
        if (newPos <= tr.doc.content.size) {
          tr.setSelection(
            state.selection.constructor.near(tr.doc.resolve(newPos))
          );
        }
        view.dispatch(tr);

        // Set recording start position if this is the first content
        if (isFirstRecordedContent) {
          recordingStartPos.current = insertionPos;
          console.log(
            "First recorded content starts at position:",
            recordingStartPos.current
          );
        }

        // Update recording end position if we're recording
        if (isRecording) {
          recordingEndPos.current = insertionPos + insertedNodeSize;
          console.log(
            "Updated recording end position to:",
            recordingEndPos.current
          );
        }
        return;
      } catch (error) {
        console.warn(
          "Failed to insert after list item, falling back to end of document"
        );
        // Fall through to default insertion at end
      }
    }

    // Check if we're in a blockquote
    if (
      parent.type.name === "blockquote" ||
      $from.node(-1)?.type.name === "blockquote"
    ) {
      // Add a new paragraph inside the blockquote after current paragraph
      const paragraphDepth =
        parent.type.name === "paragraph" ? $from.depth : $from.depth - 1;

      try {
        const afterParagraphPos = $from.after(paragraphDepth);
        insertionPos = afterParagraphPos; // Track where we're inserting

        node = state.schema.nodes.paragraph.create(
          null,
          state.schema.text(content)
        );
        insertedNodeSize = node.nodeSize;

        // Insert after the current paragraph in the blockquote
        const tr = state.tr.insert(afterParagraphPos, node);
        view.dispatch(tr);

        // Set recording start position if this is the first content
        if (isFirstRecordedContent) {
          recordingStartPos.current = insertionPos;
          console.log(
            "First recorded content starts at position:",
            recordingStartPos.current
          );
        }

        // Update recording end position if we're recording
        if (isRecording) {
          recordingEndPos.current = insertionPos + insertedNodeSize;
          console.log(
            "Updated recording end position to:",
            recordingEndPos.current
          );
        }
        return;
      } catch (error) {
        console.warn(
          "Failed to insert after blockquote paragraph, falling back to end of document"
        );
        // Fall through to default insertion at end
      }
    }

    // Check if we're in a code block
    if (parent.type.name === "codeBlock") {
      // Insert a new line in the code block at the end
      try {
        const endOfCodeBlock = $from.after() - 1;
        insertionPos = endOfCodeBlock; // Track where we're inserting
        const insertedText = "\n" + content;
        insertedNodeSize = insertedText.length;

        const tr = state.tr.insertText(insertedText, endOfCodeBlock);
        view.dispatch(tr);

        // Set recording start position if this is the first content
        if (isFirstRecordedContent) {
          recordingStartPos.current = insertionPos;
          console.log(
            "First recorded content starts at position:",
            recordingStartPos.current
          );
        }

        // Update recording end position if we're recording
        if (isRecording) {
          recordingEndPos.current = insertionPos + insertedNodeSize;
          console.log(
            "Updated recording end position to:",
            recordingEndPos.current
          );
        }
        return;
      } catch (error) {
        console.warn(
          "Failed to insert in code block, falling back to end of document"
        );
        // Fall through to default insertion at end
      }
    }

    // Check if we're in a heading
    if (parent.type.name === "heading") {
      const level = parent.attrs.level || 2;
      node = state.schema.nodes.heading.create(
        { level },
        state.schema.text(content)
      );
    }
    // Default to paragraph
    else {
      node = state.schema.nodes.paragraph.create(
        null,
        state.schema.text(content)
      );
    }

    insertedNodeSize = node.nodeSize;

    // Safe insertion at the end of document or after current block
    try {
      // First try to insert after the current block
      if ($from.depth > 0) {
        const endOfBlock = $from.after($from.depth);
        if (endOfBlock <= state.doc.content.size) {
          insertionPos = endOfBlock; // Track where we're inserting

          const tr = state.tr.insert(endOfBlock, node);

          // Move cursor to the end of the new node - with bounds checking
          const newPos = endOfBlock + node.nodeSize;
          if (newPos <= tr.doc.content.size) {
            tr.setSelection(
              state.selection.constructor.near(tr.doc.resolve(newPos))
            );
          }

          view.dispatch(tr);

          // Set recording start position if this is the first content
          if (isFirstRecordedContent) {
            recordingStartPos.current = insertionPos;
            console.log(
              "First recorded content starts at position:",
              recordingStartPos.current
            );
          }

          // Update recording end position if we're recording
          if (isRecording) {
            recordingEndPos.current = insertionPos + insertedNodeSize;
            console.log(
              "Updated recording end position to:",
              recordingEndPos.current
            );
          }
          return;
        }
      }

      // Fallback: Insert at the very end of the document
      const endPos = state.doc.content.size;
      insertionPos = endPos; // Track where we're inserting

      const tr = state.tr.insert(endPos, node);

      // Move cursor to the end of the new node
      const newPos = endPos + node.nodeSize;
      if (newPos <= tr.doc.content.size) {
        tr.setSelection(
          state.selection.constructor.near(tr.doc.resolve(newPos))
        );
      }

      view.dispatch(tr);

      // Set recording start position if this is the first content
      if (isFirstRecordedContent) {
        recordingStartPos.current = insertionPos;
        console.log(
          "First recorded content starts at position:",
          recordingStartPos.current
        );
      }

      // Update recording end position if we're recording
      if (isRecording) {
        recordingEndPos.current = insertionPos + insertedNodeSize;
        console.log(
          "Updated recording end position to:",
          recordingEndPos.current
        );
      }
    } catch (error) {
      console.error("Failed to insert content in TipTap editor:", error);

      // Last resort: Just append text at the current cursor position
      try {
        const currentPos = $from.pos;
        const insertedText = "\n" + content;
        insertedNodeSize = insertedText.length;

        const tr = state.tr.insertText(insertedText, currentPos);
        view.dispatch(tr);

        // Set recording start position if this is the first content
        if (isFirstRecordedContent) {
          recordingStartPos.current = currentPos;
          console.log(
            "First recorded content starts at position (fallback):",
            recordingStartPos.current
          );
        }

        // Update recording end position if we're recording
        if (isRecording) {
          recordingEndPos.current = currentPos + insertedNodeSize;
          console.log(
            "Updated recording end position to (fallback):",
            recordingEndPos.current
          );
        }
      } catch (finalError) {
        console.error("Final fallback failed:", finalError);
      }
    }
  };

  /**
   * Selects all content that was recorded during the recording session
   */
  const selectRecordedContent = () => {
    if (!mainTipTapEditor) return;

    if (
      recordingStartPos.current !== null &&
      recordingEndPos.current !== null &&
      recordingEndPos.current > recordingStartPos.current
    ) {
      try {
        const { state } = mainTipTapEditor;
        const from = Math.min(
          recordingStartPos.current,
          state.doc.content.size
        );
        const to = Math.min(recordingEndPos.current, state.doc.content.size);

        mainTipTapEditor.chain().focus().setTextSelection({ from, to }).run();
      } catch (error) {
        console.error("Error selecting recorded content:", error);
      }
    }
  };

  // ========== Effects ==========

  /**
   * Handle recording state changes
   */
  useEffect(() => {
    if (!mainTipTapEditor) return;

    // Recording just started
    if (promptGeneratorEnabled && !previousPromptGeneratorEnabled.current) {
      // Reset the recording positions - they will be set when content is added
      recordingStartPos.current = null;
      recordingEndPos.current = null;
      console.log("Recording started, waiting for first content...");
    }

    // Recording just stopped - select all recorded content
    if (!promptGeneratorEnabled && previousPromptGeneratorEnabled.current) {
      console.log("Recording stopped");
      console.log("Start position:", recordingStartPos.current);
      console.log("End position:", recordingEndPos.current);

      // Automatically select everything that was added during recording
      if (
        recordingStartPos.current !== null &&
        recordingEndPos.current !== null &&
        recordingEndPos.current > recordingStartPos.current
      ) {
        try {
          const { state } = mainTipTapEditor;
          const from = Math.min(
            recordingStartPos.current,
            state.doc.content.size
          );
          const to = Math.min(recordingEndPos.current, state.doc.content.size);

          // Chain focus and setTextSelection commands
          mainTipTapEditor.chain().focus().setTextSelection({ from, to }).run();

          console.log(`Selected recorded content from ${from} to ${to}`);
        } catch (error) {
          console.error("Error selecting recorded content:", error);
        }
      }
    }

    previousPromptGeneratorEnabled.current = promptGeneratorEnabled;
  }, [promptGeneratorEnabled, mainTipTapEditor]);

  /**
   * Main interaction handler effect
   */
  useEffect(() => {
    const div = wrapperRef.current;
    if (!div || (!gptDriverSimulatorData && !appetizeSession?.getData?.()))
      return;

    // Get the appropriate session data based on flag
    const sessionData = usingGptDriverSimulator
      ? {
          commands: gptDriverSimulatorData.commands,
          device: gptDriverSimulatorData.device,
        }
      : {
          commands: appetizeSession.getData?.(),
          device: appetizeSession.getData?.()?.device,
        };

    // ========== Focus Management ==========
    const blurOnOutsideInteraction$ = merge(
      fromEvent<MouseEvent>(document, "mousedown"),
      fromEvent<TouchEvent>(document, "touchstart")
    ).pipe(
      tap((event) => {
        const target = event.target as HTMLElement;
        if (!div.contains(target)) {
          setIsFocused(false);
          isFocusedRef.current = false; // Immediately update the ref
        }
      })
    );

    // Also remove focus on Escape key
    const escapeBlur$ = fromEvent<KeyboardEvent>(document, "keydown").pipe(
      filter((event) => event.key === "Escape"),
      tap(() => {
        setIsFocused(false);
        isFocusedRef.current = false; // Immediately update the ref
      })
    );

    // ========== Text Input Streams ==========
    const paste$ = fromEvent<ClipboardEvent>(document, "paste").pipe(
      filter(() => isFocusedRef.current),
      map((event) => ({
        source: "user" as EventSource,
        type: "text" as const,
        data: { content: event.clipboardData?.getData("text") || "" },
      }))
    );

    const keydown$ = fromEvent<KeyboardEvent>(document, "keydown").pipe(
      filter((event) => {
        if (!isFocusedRef.current) return false;
        if (event.metaKey || event.ctrlKey) return false;
        return (
          event.key === "Backspace" ||
          event.key === " " ||
          PRINTABLE_CHAR_REGEX.test(event.key)
        );
      }),
      bufferWhen(() => {
        // Only close buffer on debounce or backspace (not space)
        return merge(
          fromEvent<KeyboardEvent>(document, "keydown").pipe(
            debounceTime(1000)
          ),
          fromEvent<KeyboardEvent>(document, "keydown").pipe(
            filter((event) => event.key === "Backspace") // Removed space from here
          )
        );
      }),
      filter((events) => events.length > 0),
      mergeMap((events) => {
        const actions: SimulatorEvent[] = [];
        let currentText = "";

        events.forEach((event) => {
          if (PRINTABLE_CHAR_REGEX.test(event.key) || event.key === " ") {
            // Include spaces in the text instead of treating them as separate events
            currentText += event.key;
          } else if (event.key === "Backspace") {
            if (currentText) {
              actions.push({
                source: "user",
                type: "text",
                data: { content: currentText },
              });
              currentText = "";
            }
            actions.push({
              source: "user",
              type: "delete",
            });
          }
        });

        if (currentText) {
          actions.push({
            source: "user",
            type: "text",
            data: { content: currentText },
          });
        }

        return from(actions);
      })
    );
    // ========== Event Processor ==========
    const processEvent = (event: SimulatorEvent) => {
      // User events - execute simulator commands and log
      switch (event.type) {
        case "text":
          return from(
            (usingGptDriverSimulator
              ? sessionData.commands.type({ text: event.data.content })
              : sessionData.commands.type(event.data.content)
            ).then(() => {
              if (promptGeneratorEnabled) {
                const command = `type: "${event.data.content
                  .replace("\r", "")
                  .replace("\n", "")}"`;
                addTipTapEditorContent(command);
              }
            })
          );

        case "space":
          if (usingGptDriverSimulator) {
            return from(
              sessionData.commands
                .keyStroke({
                  keycode: sessionData.device.platform === "android" ? 62 : 49,
                })
                .then(() => {
                  if (promptGeneratorEnabled) {
                    addTipTapEditorContent("press space");
                  }
                })
            );
          } else {
            return from(
              sessionData.commands.keypress(" ").then(() => {
                if (promptGeneratorEnabled) {
                  addTipTapEditorContent("press space");
                }
              })
            );
          }

        case "delete":
          if (usingGptDriverSimulator) {
            return from(
              sessionData.commands
                .keyStroke({
                  keycode: sessionData.device.platform === "android" ? 67 : 42,
                })
                .then(() => {
                  if (promptGeneratorEnabled) {
                    addTipTapEditorContent("press backspace");
                  }
                })
            );
          } else {
            return from(
              sessionData.commands.keypress("Backspace").then(() => {
                if (promptGeneratorEnabled) {
                  addTipTapEditorContent("press backspace");
                }
              })
            );
          }

        default:
          return EMPTY;
      }
    };

    const textEvents$ = merge(keydown$, paste$).pipe(concatMap(processEvent));

    // ========== Mouse/Touch Streams ==========
    const mouseDown$ = fromEvent<MouseEvent>(div, "mousedown");
    const mouseMove$ = fromEvent<MouseEvent>(div, "mousemove");
    const mouseUp$ = fromEvent<MouseEvent>(div, "mouseup");
    const mouseLeave$ = fromEvent<MouseEvent>(div, "mouseleave");
    const touchStart$ = fromEvent<TouchEvent>(div, "touchstart");
    const touchMove$ = fromEvent<TouchEvent>(div, "touchmove");
    const touchEnd$ = fromEvent<TouchEvent>(div, "touchend");

    // Start gesture
    const start$ = merge(
      mouseDown$.pipe(map((e) => ({ type: "mouse" as const, event: e }))),
      touchStart$.pipe(map((e) => ({ type: "touch" as const, event: e })))
    ).pipe(
      tap(({ type, event }) => {
        // Set focus immediately when starting interaction with the device
        setIsFocused(true);
        isFocusedRef.current = true; // Immediately update the ref for synchronous access

        const rect = div.getBoundingClientRect();
        const coords = type === "mouse" ? event : event.touches[0];

        const startX = coords.clientX - rect.left;
        const startY = coords.clientY - rect.top;

        touchState$.next({
          startTime: Date.now(),
          isTouching: true,
          isSwipe: false,
          startElementCoords: { x: startX, y: startY },
        });

        setCursorPos({ x: startX, y: startY });
        setIsTapping(true);
      })
    );

    // Move gesture
    const move$ = merge(
      mouseMove$.pipe(map((e) => ({ type: "mouse" as const, event: e }))),
      touchMove$.pipe(map((e) => ({ type: "touch" as const, event: e })))
    ).pipe(
      withLatestFrom(touchState$),
      filter(([_, state]) => state.isTouching),
      tap(([{ type, event }, state]) => {
        const rect = div.getBoundingClientRect();
        const coords = type === "mouse" ? event : event.touches[0];

        const newPos = {
          x: coords.clientX - rect.left,
          y: coords.clientY - rect.top,
        };

        const deltaX = newPos.x - state.startElementCoords.x;
        const deltaY = newPos.y - state.startElementCoords.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > TAP_MOVEMENT_THRESHOLD) {
          if (distance >= SWIPE_THRESHOLD) {
            touchState$.next({ ...state, isSwipe: true });
            setTrail({
              startX: state.startElementCoords.x,
              startY: state.startElementCoords.y,
              endX: newPos.x,
              endY: newPos.y,
            });
          }
          setCursorPos(newPos);
        }
      })
    );

    // End gesture
    const end$ = merge(
      mouseUp$.pipe(map((e) => ({ type: "mouse" as const, event: e }))),
      mouseLeave$.pipe(map((e) => ({ type: "mouse" as const, event: e }))),
      touchEnd$.pipe(map((e) => ({ type: "touch" as const, event: e })))
    ).pipe(
      withLatestFrom(touchState$),
      tap(() => {
        // Immediately reset visual state
        touchState$.next({
          startTime: 0,
          isTouching: false,
          isSwipe: false,
          startElementCoords: { x: 0, y: 0 },
        });
        setTrail(null);
        setIsTapping(false);
      }),
      concatMap(([{ type, event }, state]) => {
        const rect = div.getBoundingClientRect();
        const coords = type === "mouse" ? event : event.changedTouches[0];

        const endX = coords.clientX - rect.left;
        const endY = coords.clientY - rect.top;

        const duration = Date.now() - state.startTime;
        const deltaX = endX - state.startElementCoords.x;
        const deltaY = endY - state.startElementCoords.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Handle swipe
        if (state.isSwipe) {
          const startXPercent = state.startElementCoords.x / rect.width;
          const startYPercent = state.startElementCoords.y / rect.height;
          const endXPercent = endX / rect.width;
          const endYPercent = endY / rect.height;

          const startDeviceX = Math.round(
            startXPercent * sessionData.device.screen.width
          );
          const startDeviceY = Math.round(
            startYPercent * sessionData.device.screen.height
          );
          const endDeviceX = Math.round(
            endXPercent * sessionData.device.screen.width
          );
          const endDeviceY = Math.round(
            endYPercent * sessionData.device.screen.height
          );

          if (usingGptDriverSimulator) {
            return from(
              sessionData.commands
                .swipe({
                  startCoordinates: { x: startDeviceX, y: startDeviceY },
                  endCoordinates: { x: endDeviceX, y: endDeviceY },
                  duration:
                    companyData.settings.customSlideDurations[
                      platform.toLowerCase()
                    ] ?? duration,
                })
                .then(() => {
                  if (promptGeneratorEnabled) {
                    const moveX = startDeviceX - endDeviceX;
                    const moveY = startDeviceY - endDeviceY;

                    let command;
                    if (Math.abs(moveX) > Math.abs(moveY)) {
                      const direction = moveX > 0 ? "right" : "left";
                      const percentage = Math.round(
                        (Math.abs(moveX) / sessionData.device.screen.width) *
                          100
                      );

                      if (
                        startDeviceY < 0.2 * sessionData.device.screen.height ||
                        startDeviceY > 0.8 * sessionData.device.screen.height ||
                        percentage < 20
                      ) {
                        command = `slide ${direction} ${percentage}% ; at x=${startDeviceX} y=${startDeviceY}`;
                      } else {
                        command = `swipe ${direction}`;
                      }
                    } else {
                      const direction = moveY > 0 ? "down" : "up";
                      command = `scroll ${direction}`;
                    }

                    addTipTapEditorContent(command);
                  }
                })
            );
          } else {
            // For appetize session, use the swipe method
            return from(
              sessionData.commands
                .swipe({
                  gesture: (g) => {
                    const moveX = startDeviceX - endDeviceX;
                    const moveY = startDeviceY - endDeviceY;

                    if (Math.abs(moveX) > Math.abs(moveY)) {
                      const direction = moveX > 0 ? "left" : "right";
                      const percentage = Math.round(
                        (Math.abs(moveX) / sessionData.device.screen.width) *
                          100
                      );
                      return g[direction](`${percentage}%`);
                    } else {
                      const direction = moveY > 0 ? "up" : "down";
                      const percentage = Math.round(
                        (Math.abs(moveY) / sessionData.device.screen.height) *
                          100
                      );
                      return g[direction](`${percentage}%`);
                    }
                  },
                  position: {
                    x: `${startXPercent * 100}%`,
                    y: `${startYPercent * 100}%`,
                  },
                  duration:
                    companyData.settings.customSlideDurations[
                      platform.toLowerCase()
                    ] ?? duration,
                })
                .then(() => {
                  if (promptGeneratorEnabled) {
                    const moveX = startDeviceX - endDeviceX;
                    const moveY = startDeviceY - endDeviceY;

                    let command;
                    if (Math.abs(moveX) > Math.abs(moveY)) {
                      const direction = moveX > 0 ? "right" : "left";
                      const percentage = Math.round(
                        (Math.abs(moveX) / sessionData.device.screen.width) *
                          100
                      );

                      if (
                        startDeviceY < 0.2 * sessionData.device.screen.height ||
                        startDeviceY > 0.8 * sessionData.device.screen.height ||
                        percentage < 20
                      ) {
                        command = `slide ${direction} ${percentage}% ; at x=${startDeviceX} y=${startDeviceY}`;
                      } else {
                        command = `swipe ${direction}`;
                      }
                    } else {
                      const direction = moveY > 0 ? "down" : "up";
                      command = `scroll ${direction}`;
                    }

                    addTipTapEditorContent(command);
                  }
                })
            );
          }
        }
        // Handle tap
        else if (
          distance <= TAP_MOVEMENT_THRESHOLD &&
          duration <= TAP_TIMEOUT
        ) {
          // Ensure focus is set (though it should already be set by start$)
          setIsFocused(true);
          isFocusedRef.current = true; // Immediately update the ref
          const xPercent = endX / rect.width;
          const yPercent = endY / rect.height;

          const deviceX = Math.round(
            xPercent * sessionData.device.screen.width
          );
          const deviceY = Math.round(
            yPercent * sessionData.device.screen.height
          );

          return from(
            sessionData.commands
              .tap({
                coordinates: { x: deviceX, y: deviceY },
                duration: 200,
              })
              .then(() => {
                if (promptGeneratorEnabled) {
                  let command = `tap at x=${deviceX} y=${deviceY}`;

                  let hoveredElement;
                  if (deviceScreenRef.current != null) {
                    const mouseCoords = {
                      x: endX,
                      y: endY,
                      elementWidth: rect.width,
                      elementHeight: rect.height,
                      isOver: true,
                      // Other properties can be null/false as they're not used
                      pageX: null,
                      pageY: null,
                      clientX: coords.clientX,
                      clientY: coords.clientY,
                      screenX: null,
                      screenY: null,
                      isDown: false,
                      isTouch: type === "touch",
                    };
                    hoveredElement = getHoveredElement({
                      dimensions: {
                        width: deviceScreenRef.current.width,
                        height: deviceScreenRef.current.height,
                      },
                      elements: deviceScreenRef.current.elements,
                      mouse: mouseCoords,
                      usingGptDriverDeviceProvider: usingGptDriverSimulator,
                    });
                  }
                  if (hoveredElement?.resourceId) {
                    command = `tapOn.id: "${hoveredElement?.resourceId}"`;
                  } else if (
                    hoveredElement?.text ??
                    hoveredElement?.accessibilityText
                  ) {
                    command = `tapOn: "${(hoveredElement.text ??
                      hoveredElement.accessibilityText)!}"`;
                  }
                  addTipTapEditorContent(command);
                }
              })
          );
        }

        return EMPTY;
      })
    );

    // ========== Mutation Observer Stream ==========
    const mutationObserver$ = new Observable((observer) => {
      const checkForStreamElements = () => {
        if (div) {
          const canvases = div.getElementsByTagName("canvas");
          const videos = div.getElementsByTagName("video");
          setHasStreamElement(
            (usingGptDriverSimulator
              ? gptDriverSimulatorData != null
              : appetizeSession?.getData() != null) &&
              (canvases.length > 0 || videos.length > 0)
          );
          if (
            (usingGptDriverSimulator
              ? gptDriverSimulatorData != null
              : appetizeSession?.getData() != null) &&
            canvases.length > 0 &&
            canvases.item(0)!.style.display === "none"
          ) {
            canvases.item(0)!.style.display = "block";
          }
        }
      };

      checkForStreamElements();

      const mutationObserver = new MutationObserver(checkForStreamElements);
      mutationObserver.observe(div, {
        childList: true,
        subtree: true,
      });

      return () => mutationObserver.disconnect();
    });

    // ========== Combine All Streams ==========
    const subscription = merge(
      mutationObserver$,
      blurOnOutsideInteraction$,
      escapeBlur$,
      ...(promptGeneratorEnabled ||
      !(deviceContextState?.uiInspectorEnabled ?? false)
        ? [textEvents$, start$, move$, end$]
        : [])
    )
      .pipe(takeUntil(destroy$))
      .subscribe({
        error: (err) => console.error("Interaction error:", err),
      });

    return () => subscription.unsubscribe();
  }, [
    wrapperRef,
    gptDriverSimulatorData,
    appetizeSession,
    usingGptDriverSimulator,
    companyData,
    platform,
    promptGeneratorEnabled,
    deviceScreen,
    deviceContextState,
  ]);

  /**
   * Reset on simulator change
   */
  useEffect(() => {
    if (!gptDriverSimulatorData && !appetizeSession?.getData?.()) {
      setHasStreamElement(false);
    }
    return () => setHasStreamElement(false);
  }, [gptDriverSimulatorData, appetizeSession]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      selectRecordedContent();

      destroy$.next();
      destroy$.complete();
    };
  }, [mainTipTapEditor]);

  return {
    isFocused,
    cursorPos,
    trail,
    isTapping,
    hasStreamElement,
    selectRecordedContent,
  };
};
